<template>
    <v-container class="grey-lighten-3 full-height">
        <!-- Action Toolbar (Hidden on Print) -->
        <v-row class="d-print-none mb-4" justify="center">
            <v-col cols="12" md="8" lg="9" class="d-flex justify-center gap-2">
                <v-btn color="success" prepend-icon="mdi-download" @click="downloadPDF">
                    Download PDF
                </v-btn>
            </v-col>
        </v-row>

        <!-- Printable Area -->
        <v-row justify="center">
            <v-col cols="12" xl="10" lg="11" md="12">
                <v-sheet class="report-paper pa-5 mx-auto" elevation="3" id="printable-area">

                <div class="header-section d-flex justify-space-between mb-6 lines">
                    <div class="company-logo-area d-flex align-center">
                    <!-- Replace with actual logo -->
                        <div class="logo-placeholder mr-4">
                            <!-- <v-icon icon="mdi-domain" size="40" color="grey-darken-2"></v-icon> -->
                        </div>
                        <div>
                            <v-img
                                :src="`${getFileServerUrl.replace(/\/$/, '')}/${depreciationData?.branch?.company?.logo || 'uploads/demo_images/user.png'}`"
                                width="200" height="100" class="rounded" />
                        </div>
                    </div>

                    <div class="company-address text-caption text-right">
                        <h1 class="company-name text-h5 font-weight-bold text-uppercase">
                            {{ depreciationData?.branch?.company?.name }}
                        </h1>
                        <h1 class="text-subtitle-1 text-end">
                            {{ depreciationData?.branch?.company?.address }}
                        </h1>
                    </div>
                </div>

                <!-- Title -->
                <div class="text-center mb-6">
                    <h2 class="report-title text-h6 font-weight-bold text-decoration-underline">
                        Asset Depreciation Report
                    </h2>
                </div>

                <!-- Master Information -->
                <div class="meta-info-box mb-4">
                    <div class="d-flex">
                        <div class="meta-item d-flex align-center" style="width: 50%; border-right: 1px solid #000; border-bottom:1px solid #000">
                            <span class="meta-label pl-2">Depreciation Code:</span>
                            <span class="meta-value pl-2 font-weight-bold">{{ depreciationData?.code }}</span>
                        </div>  
                        <div class="meta-item d-flex align-center" style="width: 50%; border-bottom:1px solid #000">
                            <span class="meta-label pl-2">Period Type:</span>
                            <span class="meta-value pl-2">{{ depreciationData?.period_type }}</span>
                        </div>
                    </div>
                    <div class="d-flex">
                        <div class="meta-item d-flex align-center" style="width: 33.3%; border-right: 1px solid #000;">
                            <span class="meta-label pl-2">Start Date:</span>
                            <span class="meta-value pl-2">{{ formattedDate(depreciationData?.start_date) }}</span>
                        </div>
                        <div class="meta-item d-flex align-center" style="width: 33.3%; border-right: 1px solid #000;">
                            <span class="meta-label pl-2">End Date:</span>
                            <span class="meta-value pl-2">{{ formattedDate(depreciationData?.end_date) }}</span>
                        </div>
                        <div class="meta-item d-flex align-center" style="width: 33.3%;">
                            <span class="meta-label pl-2">Depreciation Date:</span>
                            <span class="meta-value pl-2">{{ formattedDate(depreciationData?.depreciation_date) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Depreciation Details Table -->
                <div class="table-container mb-6">
                    <table class="dep-table">
                        <thead>
                            <tr>
                            <th class="text-center" style="width: 40px;">SL</th>
                            <th>Asset Name</th>
                            <th>Code</th>
                            <th>Category</th>
                            <th>Location</th>
                            <th>Method</th>
                            <th class="text-right" style="width: 120px;">Accumulated (৳)</th>
                            <th class="text-right" style="width: 110px;">Amount (৳)</th>
                            <th class="text-right" style="width: 110px;">Book Value (৳)</th>
                            <th class="text-right" style="width: 80px;">Cost (৳)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(detail, index) in depreciationData?.details" :key="detail.id">
                                <td class="text-center">{{ index + 1 }}</td>
                                <td>{{ detail.asset_register.name }}</td>
                                <td class="text-center">{{ detail.asset_register.code }}</td>
                                <td>{{ detail.asset_register.asset_category.name }}</td>
                                <td>{{ detail.asset_register.asset_location.name }}</td>
                                <td>{{ detail.asset_register.asset_depreciation_method.name }}</td>
                                <td class="text-right">{{ formatNumber(detail.amount) }}</td>
                                <td class="text-right">{{ formatNumber(detail.accumulated_depreciation) }}</td>
                                <td class="text-right">{{ formatNumber(detail.asset_register.book_value_start) }}</td>
                                <td class="text-right">{{ formatNumber(detail.asset_register.purchase_cost) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Prepared By Info -->
                <div class="prepared-section mb-8">
                    <div class="font-weight-bold mb-1">Prepared By:</div>
                    <div>{{ depreciationData?.created_by?.full_name || '-' }}</div>
                    <div class="text-caption">{{ formatDateTime(depreciationData?.created_at) }}</div>
                </div>

                <!-- Asset Image -->
                <div class="text-center mt-4">
                    <v-img
                    v-if="depreciationData?.details?.[0]?.asset_register?.image"
                    :src="`/images/assets/${depreciationData.details[0].asset_register.image}`"
                    max-width="220"
                    class="rounded-lg elevation-2"
                    contain
                    ></v-img>
                    <div v-else class="text-grey mt-2 text-caption">(No image available)</div>
                </div>

                </v-sheet>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import formattedDate from "@/plugins/dateFormatter";
import html2pdf from "html2pdf.js";

export default {
    name: "AssetDepreciationReport",
    data: () => ({
        mainId: null,
        depreciationData: null,
    }),
    
    watch: {},

    created() {
        const id = this.$route.params.id;
        this.mainId = id;
        this.$store.dispatch("getAssetDepreciationById", id).then((response) => {
            this.depreciationData = response.data;
        });
    },
    computed: {
        getFileServerUrl() {
            return this.$store.getters.getFileServerUrl;
        },
    },  

    methods: {
        formattedDate,
        formatDateTime(dateTime) {
            if (!dateTime) return "-";
            const d = new Date(dateTime);
            return `${this.formattedDate(dateTime)} ${d.toLocaleTimeString()}`;
        },
        formatNumber(num) {
            return num ? Number(num).toFixed(2) : "0.00";
        },
        async downloadPDF() {
            const element = document.getElementById("printable-area");
            const opt = {
                margin: [0.5, 0.5],
                filename: `asset-depreciation-${this.depreciationData?.code}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
            };
            await html2pdf().set(opt).from(element).save();
        },
    },
};
</script>

<style scoped>
.header-section {
    display: flex;
    flex-direction: flex !important;
}
.company-address {
    text-align: left !important;
}
.full-height {
    min-height: 100vh;
}
.grey-lighten-3 {
    background-color: #eeeeee !important;
}
.report-paper {
    background-color: white;
    color: black;
    font-family: 'Times New Roman', serif;
    max-width: 280mm;
    width: 100%;
    min-height: 297mm;
}
.company-name {
    color: #444;
    letter-spacing: 1px;
}
.report-title {
    text-decoration: underline;
    text-underline-offset: 4px;
}
.meta-info-box {
    border: 1px solid #000;
}
.meta-item {
    height: 35px;
}
.meta-label {
    font-weight: 500;
    color: #333;
}
.dep-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.dep-table th {
    background-color: #e0e0e0;
    border: 1px solid #000;
    padding: 6px 5px;
}
.dep-table td {
    border: 1px solid #000;
    padding: 6px 5px;
    vertical-align: top;
}
.lines {
    border-bottom: 1px solid #ccc;
    padding-bottom: 5px;
}
.text-center {
    text-align: center;
}
.text-right {
    text-align: right;
}
.gap-2 {
    gap: 8px;
}

/* Print Styles */
@media print {
    body * {
            visibility: hidden;
    }
    #printable-area,
    #printable-area * {
            visibility: visible;
    }
    #printable-area {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        padding: 15px;
    }
    .report-paper {
        box-shadow: none !important;
    }
    @page {
        size: A4 portrait;
        margin: 0.5cm;
    }
    .d-print-none {
        display: none !important;
    }
}

</style>
